import {
  requiresLpgCargoRatioForCargo,
  usesDyedUndyedAlpha,
} from '../../../../../shared/offer-domain/constants';
import {
  CARGO_RATIO_LABELS,
  getCargoRatioKey,
} from '../../../../../shared/offer-domain/cargoRatio';
import {
  enrichLpgDeliveryItemsWithCargoRatio,
  resolveLpgSourceParcelSize,
} from '../../../components/ModelDelivery/shared/cargoRatioUtils';
import {
  unwrapApiData,
  unwrapApiItems,
  hasCounterOffer,
} from '../../../components/ModelDelivery/shared/utils';
import { collectOriginalCargoRatios, getRowKey } from '../shared';

const getExpandKey = row =>
  String(
    row.quantityIncotermId ??
      `${row.incoterm ?? ''}-${row.route ?? ''}-${row.id ?? ''}`
  );

const getGroupRowsForCounter = (items, selectedRow) =>
  items.filter(
    item =>
      String(item.quantityIncotermId ?? '') ===
      String(selectedRow.quantityIncotermId ?? '')
  );

const applyCounterAlphaState = (updatedItem, item, value, rowKey, _row) => {
  if (!hasCounterOffer(item) && getRowKey(item) !== rowKey) {
    return null;
  }
  updatedItem.counterAlpha = value;
  const ratios = Array.isArray(item.counterCargoRatios)
    ? item.counterCargoRatios.map(ratio => ({
        ...ratio,
        alpha: value,
      }))
    : item.counterCargoRatios;
  updatedItem.counterCargoRatios = ratios;
  if (Array.isArray(ratios) && ratios.length > 0) {
    updatedItem.counterRatioAlphaByKey = Object.fromEntries(
      ratios.map(ratio => [getCargoRatioKey(ratio.id), value])
    );
  }
  return updatedItem;
};

const pickCounterField = (item, camel, pascal) =>
  item?.[camel] ?? item?.[pascal] ?? null;

const buildCargoRatioTargetsFromRows = (rows, qiId, selectedRatioKeys) =>
  rows
    .filter(
      row =>
        String(row.quantityInformationId ?? '') === String(qiId) &&
        selectedRatioKeys.has(getCargoRatioKey(row.cargoRatio))
    )
    .map(row => ({
      cargoRatio: Number(getCargoRatioKey(row.cargoRatio)),
      quantityInformationCargoRatioFK: row.quantityInformationCargoRatioId,
      parcelSize: resolveLpgSourceParcelSize(row),
      alpha: row.counterAlpha ?? row.alpha ?? null,
    }))
    .filter(
      target =>
        target.quantityInformationCargoRatioFK &&
        Number.isFinite(target.cargoRatio)
    );

const saveCargoRatioCounters = async ({
  modalFormData,
  selectedRowData,
  groupRowsForCounter,
  informationCounterIdByQi,
  quantityIncotermCounterId,
  requestCounter,
}) => {
  const selectedRatios = modalFormData.selectedRatios?.length
    ? modalFormData.selectedRatios
    : selectedRowData.cargoRatio
      ? [
          {
            id: selectedRowData.cargoRatio,
            label: selectedRowData.cargoRatioLabel,
          },
        ]
      : [];

  const selectedRatioKeys = new Set(
    selectedRatios
      .map(ratio => getCargoRatioKey(ratio?.id ?? ratio))
      .filter(Boolean)
  );

  for (const [qiId, informationCounterId] of informationCounterIdByQi) {
    const qiRow =
      groupRowsForCounter.find(
        item => String(item.quantityInformationId) === String(qiId)
      ) || selectedRowData;

    const cargoRatioTargets = buildCargoRatioTargetsFromRows(
      groupRowsForCounter,
      qiId,
      selectedRatioKeys
    );

    if (!cargoRatioTargets.length) continue;

    // Difilter per incoterm counter: tanpa itu, sapuan hapus di bawah akan membuang baris
    // milik incoterm counter lain pada parcel counter yang sama.
    const existingCargoRatioResult = await requestCounter(
      `quantity-information-cargo-ratio-counter?QuantityInformationCounterFK=${encodeURIComponent(informationCounterId)}` +
        (quantityIncotermCounterId
          ? `&QuantityIncotermCounterFK=${encodeURIComponent(quantityIncotermCounterId)}`
          : '')
    );
    const existingCargoRatioData = unwrapApiData(existingCargoRatioResult);
    const existingCargoRatioItems = Array.isArray(existingCargoRatioData)
      ? existingCargoRatioData
      : (existingCargoRatioData?.items ??
        unwrapApiItems(existingCargoRatioResult) ??
        []);

    const selectedFkSet = new Set(
      cargoRatioTargets.map(target =>
        String(target.quantityInformationCargoRatioFK)
      )
    );

    await Promise.all(
      existingCargoRatioItems.map(item => {
        const sourceFk = String(
          pickCounterField(
            item,
            'quantityInformationCargoRatioFK',
            'QuantityInformationCargoRatioFK'
          ) ?? ''
        );
        if (sourceFk && selectedFkSet.has(sourceFk)) return null;
        const id = item?.id ?? item?.Id;
        if (!id) return null;
        return requestCounter(
          `quantity-information-cargo-ratio-counter/${id}`,
          {
            method: 'DELETE',
          }
        );
      })
    );

    await Promise.all(
      cargoRatioTargets.map(async target => {
        const existing = existingCargoRatioItems.find(item => {
          const sourceFk = String(
            pickCounterField(
              item,
              'quantityInformationCargoRatioFK',
              'QuantityInformationCargoRatioFK'
            ) ?? ''
          );
          return sourceFk === String(target.quantityInformationCargoRatioFK);
        });
        const existingId = existing?.id ?? existing?.Id;
        const existingNormalized = {
          alpha:
            existing?.alpha ??
            existing?.Alpha ??
            target.alpha ??
            qiRow.counterAlpha ??
            null,
          alphaImprovement:
            existing?.alphaImprovement ??
            existing?.AlphaImprovement ??
            target.alphaImprovement ??
            null,
          alphaHps: existing?.alphaHps ?? existing?.AlphaHps ?? null,
          alphaDyedHps:
            existing?.alphaDyedHps ?? existing?.AlphaDyedHps ?? null,
          alphaUndyedHps:
            existing?.alphaUndyedHps ?? existing?.AlphaUndyedHps ?? null,
          parcelSize:
            existing?.parcelSize ??
            existing?.ParcelSize ??
            target.parcelSize ??
            resolveLpgSourceParcelSize(qiRow),
        };
        const payload = {
          cargoRatio: target.cargoRatio,
          quantityInformationCargoRatioFK:
            target.quantityInformationCargoRatioFK,
          quantityInformationCounterFK: informationCounterId,
          quantityIncotermCounterFK: quantityIncotermCounterId,
          parcelSize: existingNormalized.parcelSize,
          alpha: existingNormalized.alpha,
          alphaImprovement: existingNormalized.alphaImprovement,
          alphaHps: existingNormalized.alphaHps,
          alphaDyedHps: existingNormalized.alphaDyedHps,
          alphaUndyedHps: existingNormalized.alphaUndyedHps,
        };

        if (existingId) {
          await requestCounter(
            `quantity-information-cargo-ratio-counter/${existingId}`,
            {
              method: 'PUT',
              body: JSON.stringify(payload),
            }
          );
          return existingId;
        }

        await requestCounter('quantity-information-cargo-ratio-counter', {
          method: 'POST',
          body: JSON.stringify(payload),
        });
        return null;
      })
    );
  }
};

const deleteCounterExtras = async ({
  rowData,
  informationId,
  incotermCounterId,
  requiresCargoRatio,
  requestCounter,
  findCounterByForeignKey,
  deleteById,
}) => {
  if (!requiresCargoRatio) return;

  const cargoRatioFk = rowData.quantityInformationCargoRatioId;
  const informationCounterIdForCargo = informationId;

  if (informationCounterIdForCargo) {
    try {
      // Parcel counter bisa dipakai beberapa incoterm counter. Hapus hanya milik incoterm
      // counter yang sedang dibatalkan, kalau tidak counter incoterm lain ikut hilang.
      const cargoRatioResult = await requestCounter(
        `quantity-information-cargo-ratio-counter?QuantityInformationCounterFK=${encodeURIComponent(informationCounterIdForCargo)}` +
          (incotermCounterId
            ? `&QuantityIncotermCounterFK=${encodeURIComponent(incotermCounterId)}`
            : '')
      );
      const cargoRatioData = unwrapApiData(cargoRatioResult);
      const cargoRatioItems = Array.isArray(cargoRatioData)
        ? cargoRatioData
        : (cargoRatioData?.items ?? unwrapApiItems(cargoRatioResult) ?? []);

      await Promise.all(
        cargoRatioItems.map(item =>
          deleteById(
            'quantity-information-cargo-ratio-counter',
            item?.id ?? item?.Id
          )
        )
      );
    } catch {
      if (cargoRatioFk) {
        const cargoRatioCounter = await findCounterByForeignKey(
          'quantity-information-cargo-ratio-counter',
          'QuantityInformationCargoRatioFK',
          cargoRatioFk
        );
        await deleteById(
          'quantity-information-cargo-ratio-counter',
          cargoRatioCounter?.id ?? cargoRatioCounter?.Id
        );
      }
    }
  } else if (cargoRatioFk) {
    const cargoRatioCounter = await findCounterByForeignKey(
      'quantity-information-cargo-ratio-counter',
      'QuantityInformationCargoRatioFK',
      cargoRatioFk
    );
    await deleteById(
      'quantity-information-cargo-ratio-counter',
      cargoRatioCounter?.id ?? cargoRatioCounter?.Id
    );
  }
};

const shouldDeleteSharedCounter = ({ rowData, items }) =>
  !items.some(
    item =>
      String(item.quantityInformationId ?? '') ===
        String(rowData.quantityInformationId ?? '') &&
      String(item.quantityIncotermId ?? '') !==
        String(rowData.quantityIncotermId ?? '') &&
      !!item.quantityIncotermCounterId
  );

const enrichOpenCounterModal = async ({
  nextForm,
  rowData,
  items,
  informationCounterId,
  requestCounter,
}) => {
  const originalRatios = collectOriginalCargoRatios(items, rowData);
  nextForm.selectedRatios = originalRatios;
  nextForm.sourceCargoRatios = originalRatios;

  if (!informationCounterId) return nextForm;

  try {
    const cargoRatioResult = await requestCounter(
      `quantity-information-cargo-ratio-counter?QuantityInformationCounterFK=${encodeURIComponent(informationCounterId)}`
    );
    const cargoRatioData = unwrapApiData(cargoRatioResult);
    const cargoRatioItems = Array.isArray(cargoRatioData)
      ? cargoRatioData
      : (cargoRatioData?.items ?? unwrapApiItems(cargoRatioResult) ?? []);

    const mappedCounterRatios = cargoRatioItems
      .map(item => {
        const cargoRatio = item?.cargoRatio ?? item?.CargoRatio ?? null;
        if (cargoRatio === null || cargoRatio === undefined) return null;
        const ratioKey = getCargoRatioKey(cargoRatio);
        const ratioId = Number(ratioKey);
        if (!Number.isFinite(ratioId) || ratioId < 1 || ratioId > 5) {
          return null;
        }
        return {
          id: ratioId,
          label:
            CARGO_RATIO_LABELS[ratioId - 1] ??
            rowData.cargoRatioLabel ??
            String(cargoRatio),
        };
      })
      .filter(Boolean);

    if (mappedCounterRatios.length) {
      nextForm.selectedRatios = mappedCounterRatios;
      nextForm.sourceCargoRatios = originalRatios;
    }
  } catch {
    nextForm.selectedRatios = originalRatios;
    nextForm.sourceCargoRatios = originalRatios;
  }

  return nextForm;
};

const buildOriginalFormExtras = ({
  rowData,
  nextForm,
  items,
  requiresCargoRatio,
}) => {
  const originalRatios =
    requiresCargoRatio && nextForm.sourceCargoRatios?.length
      ? nextForm.sourceCargoRatios
      : requiresCargoRatio
        ? collectOriginalCargoRatios(items, rowData)
        : [];

  const originalQty =
    rowData.deliveryBasis ?? rowData.parcelSize ?? nextForm.deliveryBasis;

  return {
    selectedRatios: originalRatios,
    sourceCargoRatios: originalRatios,
    deliveryBasis: originalQty,
    quantityLocation1:
      rowData.parcelSize ?? rowData.deliveryBasis ?? originalQty,
    quantityLocation2: rowData.parcelSize2 ?? '',
    quantityLocation3: rowData.parcelSize3 ?? '',
  };
};

export default {
  isLpg: true,
  getUsesAlphaField: () => true,
  // cargo ratio follows the product type of the cargo (quantity delivery term)
  getRequiresCargoRatio: (form, cargo) =>
    requiresLpgCargoRatioForCargo(form, cargo),
  requireIncotermCounter: true,
  isLpgCaseForLocations: true,
  shouldFetchPricingUnit: () => false,
  preserveQuantityOnSave: true,
  getExpandKey,
  getGroupRowsForCounter,
  getAfterSaveExpandKey: getExpandKey,
  applyCounterAlphaState,
  saveCargoRatioCounters,
  deleteCounterExtras,
  shouldDeleteSharedCounter,
  enrichOpenCounterModal,
  buildOriginalFormExtras,
  enrichAfterFob: async ({
    items,
    procurementId,
    quantityDeliveryTermId,
    requiresCargoRatio,
  }) => {
    try {
      return await enrichLpgDeliveryItemsWithCargoRatio({
        procurementId,
        quantityDeliveryTermId,
        items,
        skipCargoRatio: !requiresCargoRatio,
      });
    } catch {
      return items;
    }
  },
  getCounterFormChangeOptions: requiresCargoRatio => ({
    isLpg: true,
    requiresCargoRatio,
  }),
  getValidateCounterOptions: requiresCargoRatio => ({
    requiresCargoRatio,
    skipLocationQuantity: true,
  }),
  usesDyedUndyedAlpha,
};
